/**
 * 管理者向けダッシュボード(トップ画面)。2026-09-12 の指示に沿った構成(①〜⑩)。
 *
 * **表示は全て「集計データがある最新日までの確定値」**。BlueBean の CSV は翌朝取り込むため当日の進捗は持たない。
 * 計算は `dashboardData.ts` に集約し、この画面は並べるだけにする。金額は PL と同じ `buildPl` の結果を使う。
 * 2026-09-17 MTG: 期間(日次/週次/月次/任意期間。既定は当月)を選べるようにし、RANK は数値と同じ色、原価率を RANK の右隣に置いた。
 * 期間は URL(?mode=day|week|month|range&date=…&from=…&to=…)に持つ。
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DateField from '@core/components/DateField';
import { useToast } from '@core/components/Toast';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import { SortTh, sortRows, useSort } from '@core/components/tableTools';
import { isValidDateStr, todayStr, weekdayLabel } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import type { Company, DailySummary, DateStr, Delivery, MonthTargets } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { addDays, addMonths, daysInMonth, fmtDuration, fmtNum, fmtPct, staffMetrics, type StaffMetrics } from './calc';
import { rankClass } from './colors';
import { buildDashboard, type CompanyLine, type DashPeriod, type WarnCounts } from './dashboardData';
import { fetchMonthTargets, fetchSummariesRange } from './dashboardRepo';
import { fetchDeliveries } from './deliveryRepo';
import { fetchExpenseDaily, fetchExpenseItems, type ExpenseDaily, type ExpenseItem } from './expenseRepo';
import MiniChart from './MiniChart';
import { buildPl } from './PlPage';
import { rangeOf, shiftAnchor } from './usePeriod';

const money = (v: number | null) => (v === null ? '—' : `¥${formatYen(v)}`);
const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
const shortYen = (v: number) => (Math.abs(v) >= 10000 ? `${Math.round(v / 10000).toLocaleString('ja-JP')}万` : formatYen(v));
const n1 = (v: number | null) => (v === null ? '—' : v.toFixed(1));

const WARN_LABELS: Record<keyof WarnCounts, { label: string; to: string }> = {
  csv: { label: 'CSV未取込', to: '/import' },
  delivery: { label: '納品件数未入力', to: '/summary/deliveries' },
  expense: { label: '経費未入力', to: '/summary/deliveries' },
  kintai: { label: '勤怠未確認', to: '/summary/staff' },
};

type DashMode = DashPeriod['mode'];
const DASH_MODE_LABELS: Record<DashMode, string> = { day: '日次', week: '週次', month: '月次', range: '任意期間' };
const periodInputStyle = { height: 30, padding: '0 8px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', fontSize: 12.5 } as const;

/** from〜to に含まれる月(YYYY-MM)の一覧 */
function monthsIn(from: DateStr, to: DateStr): string[] {
  const out: string[] = [];
  for (let m = from.slice(0, 7); m <= to.slice(0, 7); m = addMonths(m, 1)) out.push(m);
  return out;
}

type CompanySortKey = 'name' | 'appointments' | 'delivered' | 'deliveryRate' | 'hitRate' | 'costPerDelivery' | 'gross' | 'grossPerHour' | 'costRate' | 'cancelRate' | 'stock';

export default function HomePage() {
  const toast = useToast();
  const today = todayStr();
  const [params, setParams] = useSearchParams();

  // ---- 期間(既定は当月) ----
  const modeParam = params.get('mode');
  const mode: DashMode = modeParam === 'day' || modeParam === 'week' || modeParam === 'month' || modeParam === 'range' ? modeParam : 'month';
  const dateParam = params.get('date');
  const anchor = (dateParam && isValidDateStr(dateParam) ? dateParam : today) as DateStr;
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const period = useMemo<DashPeriod>(() => {
    if (mode !== 'range') {
      const r = rangeOf(mode, anchor);
      return { mode, from: r.from, to: r.to, label: r.label };
    }
    let from = (fromParam && isValidDateStr(fromParam) ? fromParam : `${today.slice(0, 7)}-01`) as DateStr;
    let to = (toParam && isValidDateStr(toParam) ? toParam : today) as DateStr;
    if (from > to) [from, to] = [to, from];
    return { mode, from, to, label: `${from.replaceAll('-', '/')}〜${to.replaceAll('-', '/')}` };
  }, [mode, anchor, fromParam, toParam, today]);
  /** 期間をURLに書く。null のキーは消す(日次/週次/月次に戻したときに任意期間の from/to を残さない) */
  const setPeriodParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const month = period.to.slice(0, 7);
  /** 7日平均比のために、期間の前6日も読む */
  const loadFrom = (addDays(period.to, -6) < period.from ? addDays(period.to, -6) : period.from) as DateStr;
  const periodDays = useMemo(() => {
    let n = 0;
    for (let d = period.from; d <= period.to; d = addDays(d, 1) as DateStr) n++;
    return n;
  }, [period]);

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [targetsByMonth, setTargetsByMonth] = useState<Map<string, MonthTargets>>(new Map());
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [expenses, setExpenses] = useState<ExpenseDaily>(new Map());
  const [expenseItems, setExpenseItems] = useState<ExpenseItem[]>([]);
  const [warnData, setWarnData] = useState<{ summaries: DailySummary[]; deliveries: Delivery[]; expenses: ExpenseDaily }>({ summaries: [], deliveries: [], expenses: new Map() });
  const [costView, setCostView] = useState<'with' | 'ex'>('with');
  const { sort, toggle: toggleSort } = useSort<CompanySortKey>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const months = monthsIn(loadFrom, period.to);
    const warnFrom = addDays(today, -6) as DateStr;
    void Promise.all([
      fetchCompanies(),
      Promise.all(months.map((m) => fetchMonthTargets(m))),
      fetchSummariesRange(loadFrom, period.to),
      fetchDeliveries(loadFrom, period.to),
      fetchExpenseDaily(loadFrom, period.to),
      fetchExpenseItems(),
      // 警告バーは期間に関係なく「今日から直近7日」
      Promise.all([fetchSummariesRange(warnFrom, today), fetchDeliveries(warnFrom, today), fetchExpenseDaily(warnFrom, today)]),
    ])
      .then(([c, ts, s, d, e, it, w]) => {
        if (!alive) return;
        setCompanies(c);
        setTargetsByMonth(new Map(ts.map((t, i) => [months[i]!, t])));
        setSummaries(s);
        setDeliveries(d);
        setExpenses(e);
        setExpenseItems(it);
        setWarnData({ summaries: w[0], deliveries: w[1], expenses: w[2] });
      })
      .catch((e: unknown) => {
        if (alive) toast(e instanceof Error ? e.message : '読み込みに失敗しました');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [loadFrom, period.to, today, toast]);

  /** 目標は期間の最後の月の値(原価率目標など) */
  const targets = targetsByMonth.get(month) ?? null;
  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const allRows = useMemo(() => {
    if (!targets) return [] as StaffMetrics[];
    // 売上見込の単価の予備値は、その日の月の目標を使う
    return summaries.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, targetsByMonth.get(s.date.slice(0, 7)) ?? targets, companyMap)));
  }, [summaries, targets, targetsByMonth, companyMap]);
  const monthRows = useMemo(() => allRows.filter((r) => r.date >= period.from && r.date <= period.to), [allRows, period]);
  const monthDeliveries = useMemo(() => deliveries.filter((d) => d.date >= period.from && d.date <= period.to), [deliveries, period]);
  const monthExpenses = useMemo(() => {
    const m: ExpenseDaily = new Map();
    for (const [d, v] of expenses) if (d >= period.from && d <= period.to) m.set(d, v);
    return m;
  }, [expenses, period]);

  const cols = useMemo(() => {
    const appts = new Map<string, number>();
    for (const r of monthRows) if (r.company_id) appts.set(r.company_id, (appts.get(r.company_id) ?? 0) + r.appointments);
    const del = new Map<string, number>();
    for (const d of monthDeliveries) for (const [k, v] of Object.entries(d.counts ?? {})) del.set(k, (del.get(k) ?? 0) + v);
    return companies.filter((c) => c.status === 'active').filter((c) => (appts.get(c.id) ?? 0) > 0 || (del.get(c.id) ?? 0) > 0).sort((a, b) => a.id.localeCompare(b.id));
  }, [companies, monthRows, monthDeliveries]);

  const pl = useMemo(
    () => (targets ? buildPl(monthRows, cols, monthDeliveries, targets, period.to, period.mode === 'month' ? daysInMonth(month) : periodDays, monthExpenses, expenseItems) : null),
    [targets, monthRows, cols, monthDeliveries, period, month, periodDays, monthExpenses, expenseItems],
  );
  const plByDate = useMemo(() => {
    const m = new Map<DateStr, { salesSubtotal: number; gross: number; appointments: number }>();
    if (!targets) return m;
    for (const s of summaries) {
      if (s.date < period.from || s.date > period.to || s.staff.length === 0) continue;
      const de: ExpenseDaily = new Map();
      const v = expenses.get(s.date);
      if (v) de.set(s.date, v);
      const p = buildPl(allRows.filter((r) => r.date === s.date), cols, deliveries.filter((d) => d.date === s.date), targets, s.date, 1, de, expenseItems);
      m.set(s.date, { salesSubtotal: p.salesSubtotal, gross: p.gross, appointments: p.appointments });
    }
    return m;
  }, [targets, summaries, period, allRows, cols, deliveries, expenses, expenseItems]);

  const data = useMemo(
    () => (targets ? buildDashboard({ period, today, allRows, deliveries, targets, companies, pl, plByDate, warn: warnData }) : null),
    [targets, period, today, allRows, deliveries, companies, pl, plByDate, warnData],
  );

  /** 期間の選択(日次/週次/月次/任意期間) */
  const periodBar = (
    <div className="card" style={{ paddingTop: 10, paddingBottom: 10 }}>
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <div className="tool-filter" style={{ width: '100%' }}>
          <div className="chips" style={{ marginBottom: 0 }}>
            {(Object.keys(DASH_MODE_LABELS) as DashMode[]).map((m) => (
              <button
                key={m}
                className={`chip${mode === m ? ' on' : ''}`}
                onClick={() =>
                  setPeriodParams(m === 'range' ? { mode: m, from: period.from, to: period.to, date: null } : { mode: m, date: period.to < today ? period.to : today, from: null, to: null })
                }
              >
                {DASH_MODE_LABELS[m]}
              </button>
            ))}
          </div>
          {mode === 'range' ? (
            <>
              <DateField value={period.from} onChange={(d) => setPeriodParams({ from: d })} />
              <span>〜</span>
              <DateField value={period.to} onChange={(d) => setPeriodParams({ to: d })} />
            </>
          ) : (
            <>
              <button className="chip" onClick={() => setPeriodParams({ date: shiftAnchor(mode, anchor, -1) })} aria-label="前へ">
                <IconChevronLeft size={13} />
              </button>
              {mode === 'month' ? (
                <input type="month" value={anchor.slice(0, 7)} onChange={(e) => e.target.value && setPeriodParams({ date: `${e.target.value}-01` })} style={periodInputStyle} />
              ) : (
                <DateField value={anchor} onChange={(d) => setPeriodParams({ date: d })} />
              )}
              <button className="chip" onClick={() => setPeriodParams({ date: shiftAnchor(mode, anchor, 1) })} aria-label="次へ">
                <IconChevronRight size={13} />
              </button>
            </>
          )}
          <b style={{ fontSize: 14 }}>{period.label}</b>
        </div>
      </div>
    </div>
  );

  const companyRows = useMemo(() => {
    if (!data) return [] as CompanyLine[];
    return sortRows(data.companies, sort, (c, k) => c[k]);
  }, [data, sort]);

  if (loading || !data) {
    return (
      <div className="screen master-screen full">
        {periodBar}
        <div className="card">
          <div className="empty">読み込み中…</div>
        </div>
      </div>
    );
  }

  const warnTotal = data.warn.csv + data.warn.delivery + data.warn.expense + data.warn.kintai;
  const cost = costView === 'with' ? data.costWith : data.costEx;
  const isMonth = period.mode === 'month';

  return (
    <div className="screen master-screen full">
      {periodBar}

      {/* ---- ① 警告バー(期間に関係なく直近7日) ---- */}
      <div className={`dash-warn${warnTotal > 0 ? ' ng' : ' ok'}`}>
        {warnTotal === 0 ? (
          <span>直近7日の入力・確認はすべて済んでいます</span>
        ) : (
          <>
            <b>下の数字はまだ確定していません</b>
            {(Object.keys(WARN_LABELS) as (keyof WarnCounts)[])
              .filter((k) => data.warn[k] > 0)
              .map((k) => (
                <Link key={k} to={WARN_LABELS[k].to}>
                  {WARN_LABELS[k].label} {data.warn[k]}日
                </Link>
              ))}
          </>
        )}
        <span className="asof">{data.asOf ? `${fmtDate(data.asOf)}までの確定値` : 'データなし'}</span>
      </div>

      {/* ---- ② KPI ---- */}
      <div className="card dash-kpi">
        <div className="kpi-row">
          <div className="kpi-ttl">{isMonth ? '取得ペース' : '取得数'}</div>
          <div className="kpi-body">
            <b>
              {data.pace.actual}
              {data.pace.target !== null ? <span className="sl">/{data.pace.target}件</span> : <span className="sl">件</span>}
            </b>
            {isMonth && (
              <>
                <span>必要 {n1(data.pace.needPerDay)}件/日</span>
                <span>現在 {n1(data.pace.nowPerDay)}件/日</span>
                <span className={data.pace.gap !== null && data.pace.gap > 0 ? 'bad' : 'good'}>
                  {data.pace.gap === null ? '—' : data.pace.gap > 0 ? `不足 ${data.pace.gap.toFixed(1)}件/日` : `前倒し ${Math.abs(data.pace.gap).toFixed(1)}件/日`}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="kpi-row">
          <div className="kpi-ttl">
            原価率
            <span className="kpi-tabs">
              <button className={costView === 'with' ? 'on' : ''} onClick={() => setCostView('with')}>管理者込み</button>
              <button className={costView === 'ex' ? 'on' : ''} onClick={() => setCostView('ex')}>管理者抜き</button>
            </span>
          </div>
          <div className="kpi-body">
            <b className={cost.over !== null && cost.over > 0 ? 'bad' : ''}>{fmtPct(cost.rate)}</b>
            <span>目標 {cost.target}%</span>
            <span className={cost.over !== null && cost.over > 0 ? 'bad' : 'good'}>
              {cost.over === null ? '—' : `${cost.over > 0 ? '+' : ''}${cost.over.toFixed(1)}pt`}
            </span>
            {cost.cut !== null && <span className="bad">削減必要額 {money(cost.cut)}</span>}
          </div>
        </div>
        <div className="kpi-sub">
          <span>売上 {money(data.money.sales)}{isMonth && <i>着地 {money(data.money.salesF)}</i>}</span>
          <span>粗利 {money(data.money.gross)}{isMonth && <i>着地 {money(data.money.grossF)}</i>}</span>
          <span>営業利益 {money(data.money.op)}{isMonth && <i>着地 {money(data.money.opF)}</i>}</span>
        </div>
      </div>

      {/* ---- ③ 要対応 ---- */}
      <div className="card">
        <h3>要対応</h3>
        {data.alerts.length === 0 ? (
          <div className="empty">目標を下回っている項目はありません</div>
        ) : (
          <ul className="dash-alerts">
            {data.alerts.map((a) => (
              <li key={a.key} className={a.severity}>
                <Link to={a.to}>
                  <b>{a.label}</b>
                  <span>{a.detail}</span>
                  <IconChevronRight size={12} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---- ④ 取得 → 納品 ---- */}
      <div className="dash-grid">
        <div className="card">
          <h3>
            取得 → 納品
            <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>アポは翌日納品のため1日ずらして比べています</span>
          </h3>
          {data.flow.date === null ? (
            <div className="empty">納品の入力がまだありません</div>
          ) : (
            <div className="dash-forecast">
              <div>
                <div className="nm">{data.flow.prevDate ? `${fmtDate(data.flow.prevDate)}の取得` : '前日の取得'}</div>
                <div className="amt">{data.flow.prevAppointments}件</div>
              </div>
              <div>
                <div className="nm">{fmtDate(data.flow.date)}の納品</div>
                <div className="amt">{data.flow.delivered}件</div>
              </div>
              <div>
                <div className="nm">納品率</div>
                <div className={`amt${data.flow.rate !== null && data.flow.rate < 85 ? ' neg' : ''}`}>{fmtPct(data.flow.rate)}</div>
              </div>
            </div>
          )}
        </div>
        <div className="card">
          <h3>会社別の未納品在庫({period.label})</h3>
          {data.flow.stock.length === 0 ? (
            <div className="empty">在庫はありません</div>
          ) : (
            <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <th>会社</th>
                    <th className="num">未納品</th>
                  </tr>
                </thead>
                <tbody>
                  {data.flow.stock.map((s) => (
                    <tr key={s.company_id}>
                      <td>{s.name}</td>
                      <td className={`num${s.stock < 0 ? ' neg' : ''}`}>{s.stock}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ---- ⑤ 会社別採算 ---- */}
      <div className="card">
        <h3>
          会社別の採算({period.label})
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>取得率は参考値(1人が2社に架電した日は通話回数が1社目に寄るため)</span>
        </h3>
        <div className="table-scroll">
          <table className="fit-table">
            <thead>
              <tr>
                <SortTh label="会社名" sortKey="name" sort={sort} onSort={toggleSort} defaultDir="asc" />
                <SortTh label="取得数" sortKey="appointments" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="納品数" sortKey="delivered" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="納品率" sortKey="deliveryRate" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="取得率" sortKey="hitRate" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="件あたり原価" sortKey="costPerDelivery" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="粗利" sortKey="gross" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="粗利/稼働h" sortKey="grossPerHour" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="原価率" sortKey="costRate" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="CXL率" sortKey="cancelRate" sort={sort} onSort={toggleSort} className="num" />
                <SortTh label="未納品" sortKey="stock" sort={sort} onSort={toggleSort} className="num" />
              </tr>
            </thead>
            <tbody>
              {companyRows.map((c) => (
                <tr key={c.company_id}>
                  <td>{c.name}</td>
                  <td className="num">{c.appointments}</td>
                  <td className="num">{c.delivered ?? '—'}</td>
                  <td className={`num${c.deliveryRate !== null && c.deliveryRate < 85 ? ' neg' : ''}`}>{fmtPct(c.deliveryRate)}</td>
                  <td className="num">{fmtPct(c.hitRate)}</td>
                  <td className="num">{money(c.costPerDelivery)}</td>
                  <td className={`num${c.gross < 0 ? ' neg' : ''}`}>{money(c.gross)}</td>
                  <td className="num">{money(c.grossPerHour)}</td>
                  <td className={`num${c.costRate !== null && c.costRate > data.costWith.target ? ' neg' : ''}`}>{fmtPct(c.costRate)}</td>
                  <td className={`num${c.cancelRate !== null && c.cancelRate >= 5 ? ' neg' : ''}`}>{fmtPct(c.cancelRate)}</td>
                  <td className="num">{c.stock ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- ⑥ オペレーター ---- */}
      <div className="dash-grid">
        <div className="card">
          <h3>
            要確認のオペレーター
            <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>RANK下位・後処理が長い・直近7日平均から落ちている</span>
          </h3>
          {data.watchOps.length === 0 ? (
            <div className="empty">該当はありません</div>
          ) : (
            <div className="table-scroll">
              <table className="fit-table">
                <thead>
                  <tr>
                    <th>氏名</th>
                    <th className="num">RANK</th>
                    <th className="num">原価率</th>
                    <th className="num">見込/h</th>
                    <th className="num">7日平均比</th>
                    <th className="num">後処理</th>
                    <th className="num">人件費</th>
                    <th>理由</th>
                  </tr>
                </thead>
                <tbody>
                  {data.watchOps.map((o) => (
                    <tr key={o.op_id}>
                      <td>{o.name}</td>
                      <td className="num">
                        <span className={`badge ${rankClass(o.rank)}`}>{o.rank}</span>
                      </td>
                      <td className={`num${o.costRate !== null && o.costRate > data.costWith.target ? ' neg' : ''}`}>{fmtPct(o.costRate)}</td>
                      <td className={`num ${rankClass(o.rank)}`}>{fmtNum(o.apph, 2)}</td>
                      <td className={`num${o.vs7d !== null && o.vs7d <= 75 ? ' neg' : ''}`}>{fmtPct(o.vs7d, 0)}</td>
                      <td className="num">{fmtDuration(o.afterAvg)}</td>
                      <td className="num">{money(o.pay)}</td>
                      <td className="hint wrap">{o.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card">
          <h3>上位5名({period.label})</h3>
          <div className="table-scroll">
            <table className="fit-table">
              <thead>
                <tr>
                  <th>氏名</th>
                  <th className="num">RANK</th>
                  <th className="num">原価率</th>
                  <th className="num">見込/h</th>
                </tr>
              </thead>
              <tbody>
                {data.topOps.map((o) => (
                  <tr key={o.op_id}>
                    <td>{o.name}</td>
                    <td className="num">
                      <span className={`badge ${rankClass(o.rank)}`}>{o.rank}</span>
                    </td>
                    <td className={`num${o.costRate !== null && o.costRate > data.costWith.target ? ' neg' : ''}`}>{fmtPct(o.costRate)}</td>
                    <td className={`num ${rankClass(o.rank)}`}>{fmtNum(o.apph, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ---- ⑦ 雇用形態別 ---- */}
      <div className="dash-grid">
        <div className="card">
          <h3>雇用形態別の採算({period.label})</h3>
          <div className="table-scroll">
            <table className="fit-table">
              <thead>
                <tr>
                  <th>形態</th>
                  <th className="num">人数</th>
                  <th className="num">取得/h</th>
                  <th className="num">取得1件あたり人件費</th>
                  <th className="num">原価率</th>
                </tr>
              </thead>
              <tbody>
                {data.styles.map((s) => (
                  <tr key={s.key}>
                    <td>{s.label}</td>
                    <td className="num">{s.people}</td>
                    <td className="num">{fmtNum(s.apph, 2)}</td>
                    <td className="num">{money(s.payPerAppointment)}</td>
                    <td className={`num${s.costRate !== null && s.costRate > data.costWith.target ? ' neg' : ''}`}>{fmtPct(s.costRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <h3>RANKの構成</h3>
          <MiniChart
            type="bar"
            labels={data.ranks.map((r) => r.rank)}
            series={[{ key: 'rank', label: '人数', color: 'var(--royal)', values: data.ranks.map((r) => r.count) }]}
            format={(v) => `${v}名`}
            labelEvery={1}
            height={140}
          />
          <p className="hint" style={{ marginTop: 6 }}>B-・C に支払っている人件費 {money(data.lowRankPay)}</p>
        </div>
      </div>

      {/* ---- ⑧ 時間帯別 ---- */}
      <div className="card">
        <h3>
          時間帯別{period.mode === 'day' ? (data.asOf ? `(${fmtDate(data.asOf)})` : '') : `(${period.label})`}
          <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>
            {data.peopleIsTotal ? `稼働 延べ${data.people}名。1人あたりは延べ人数で割った値です` : `稼働 ${data.people}名。1人あたりはその日の稼働人数で割った値です`}
          </span>
        </h3>
        <div className="table-scroll">
          <table className="fit-table">
            <thead>
              <tr>
                <th>時間</th>
                {data.hours.map((h) => (
                  <th key={h.hour} className="num">{h.hour}時</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>取得数</th>
                {data.hours.map((h) => (
                  <td key={h.hour} className="num">{h.appointments}</td>
                ))}
              </tr>
              <tr>
                <th>1人あたり</th>
                {data.hours.map((h) => (
                  <td key={h.hour} className="num">{fmtNum(h.perPerson, 2)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- ⑨ 推移 ---- */}
      <div className="dash-grid">
        <div className="card">
          <h3>取得数の推移(目標ペース線)</h3>
          <MiniChart
            labels={data.trend.labels}
            series={[
              { key: 'ap', label: '取得数', color: 'var(--azure)', values: data.trend.appointments },
              { key: 'pace', label: '目標ペース', color: 'var(--muted)', values: data.trend.paceLine },
            ]}
            format={(v) => `${Math.round(v)}件`}
          />
        </div>
        <div className="card">
          <h3>原価率の推移(目標 {data.trend.costTarget}%)</h3>
          <MiniChart
            labels={data.trend.labels}
            series={[
              { key: 'cr', label: '原価率', color: 'var(--royal)', values: data.trend.costRate },
              { key: 'tg', label: '目標', color: 'var(--muted)', values: data.trend.labels.map(() => data.trend.costTarget) },
            ]}
            format={(v) => `${v.toFixed(0)}%`}
          />
        </div>
      </div>
      <div className="card">
        <h3>売上と粗利の推移</h3>
        <MiniChart
          labels={data.trend.labels}
          series={[
            { key: 'sales', label: '売上小計', color: 'var(--royal)', values: data.trend.sales },
            { key: 'gross', label: '粗利', color: 'var(--aqua)', values: data.trend.gross },
          ]}
          format={shortYen}
          height={130}
        />
      </div>

      {/* ---- ⑩ ショートカット ---- */}
      <div className="card">
        <h3>よく使う画面</h3>
        <div className="toolbar">
          <div className="tool-filter">
            <Link className="txn-link" to="/pl?mode=month">PL</Link>
            <Link className="txn-link" to="/summary/staff">日次【集計】</Link>
            <Link className="txn-link" to="/summary/deliveries">日報/サマリ</Link>
            <Link className="txn-link" to="/summary/companies">会社別</Link>
            <Link className="txn-link" to="/staff">本人成績</Link>
            <Link className="txn-link" to="/import">CSVインポート</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
